/**
 * Sidebar Dropdown Management
 *
 * Functions for managing sidebar dropdown menus with Flowbite integration.
 * Handles dropdown state persistence and restoration.
 */

import { ADMIN_DROPDOWN_IDS, DROPDOWN_IDS, FLOWBITE_TOGGLE_DELAY_MS } from "./config";
import { getDropdownStates, isDropdownOpen, saveDropdownState, setAriaExpanded } from "./state";
import type { FlowbiteCollapseInstance } from "./types";

// ============================================
// Flowbite API Access
// ============================================

/**
 * Gets a Flowbite Collapse instance by dropdown ID.
 *
 * Args:
 *   dropdownId: The ID of the dropdown element.
 *
 * Returns:
 *   FlowbiteCollapseInstance | null: The Flowbite instance, or null if not found.
 */
export function getFlowbiteCollapseInstance(dropdownId: string): FlowbiteCollapseInstance | null {
  if (typeof window.FlowbiteInstances === "undefined") {
    return null;
  }
  return window.FlowbiteInstances.getInstance("Collapse", dropdownId);
}

/**
 * Checks if the Flowbite library is initialized and available.
 *
 * Returns:
 *   boolean: True if FlowbiteInstances is available.
 */
export function isFlowbiteReady(): boolean {
  return typeof window.FlowbiteInstances !== "undefined";
}

// ============================================
// Dropdown Collapse/Expand
// ============================================

/**
 * Collapses a dropdown if it's currently visible.
 *
 * Uses Flowbite API when available, falls back to DOM manipulation.
 * Skips dropdowns that are already hidden.
 *
 * Args:
 *   dropdownId: The ID of the dropdown to collapse.
 */
function collapseDropdownIfVisible(dropdownId: string): void {
  const dropdown = document.getElementById(dropdownId);
  if (!dropdown || dropdown.classList.contains("hidden")) return;

  const collapseInstance = getFlowbiteCollapseInstance(dropdownId);
  if (collapseInstance) {
    collapseInstance.collapse();
  } else {
    dropdown.classList.add("hidden");
    const button = document.querySelector<HTMLElement>(`[data-collapse-toggle="${dropdownId}"]`);
    if (button) {
      setAriaExpanded(button, false);
    }
  }
}

/**
 * Collapses all sidebar dropdowns.
 *
 * Uses Flowbite API when available, falls back to DOM manipulation.
 * Useful when transitioning to mini mode.
 */
export function collapseAllDropdowns(): void {
  [...DROPDOWN_IDS, ...ADMIN_DROPDOWN_IDS].forEach(collapseDropdownIfVisible);
}

/**
 * Expands a dropdown by ID.
 *
 * Uses Flowbite API when available, falls back to DOM manipulation.
 *
 * Args:
 *   dropdownId: The ID of the dropdown to expand.
 */
export function expandDropdown(dropdownId: string): void {
  const dropdown = document.getElementById(dropdownId);
  if (!dropdown) return;

  const collapseInstance = getFlowbiteCollapseInstance(dropdownId);
  if (collapseInstance) {
    collapseInstance.expand();
  } else {
    // Fallback to DOM manipulation
    dropdown.classList.remove("hidden");
    const button = document.querySelector<HTMLElement>(`[data-collapse-toggle="${dropdownId}"]`);
    if (button) {
      setAriaExpanded(button, true);
    }
  }
}

/**
 * Collapses a dropdown by ID.
 *
 * Uses Flowbite API when available, falls back to DOM manipulation.
 *
 * Args:
 *   dropdownId: The ID of the dropdown to collapse.
 */
export function collapseDropdown(dropdownId: string): void {
  const dropdown = document.getElementById(dropdownId);
  if (!dropdown) return;

  const collapseInstance = getFlowbiteCollapseInstance(dropdownId);
  if (collapseInstance) {
    collapseInstance.collapse();
  } else {
    // Fallback to DOM manipulation
    dropdown.classList.add("hidden");
    const button = document.querySelector<HTMLElement>(`[data-collapse-toggle="${dropdownId}"]`);
    if (button) {
      setAriaExpanded(button, false);
    }
  }
}

// ============================================
// State Restoration
// ============================================

/**
 * Restores saved dropdown states from localStorage.
 *
 * Uses Flowbite API when available to stay in sync with internal state,
 * falls back to DOM manipulation if Flowbite is not ready.
 */
export function restoreDropdownStates(): void {
  const states = getDropdownStates();

  for (const [dropdownId, shouldBeOpen] of Object.entries(states)) {
    const dropdown = document.getElementById(dropdownId);
    if (!dropdown) continue;

    const currentlyOpen = isDropdownOpen(dropdown);

    // Only change state if it differs from current
    if (shouldBeOpen === currentlyOpen) continue;

    if (shouldBeOpen) {
      expandDropdown(dropdownId);
    } else {
      collapseDropdown(dropdownId);
    }
  }
}

// ============================================
// Event Handlers
// ============================================

/**
 * Initializes dropdown state persistence.
 *
 * Attaches click handlers to all dropdown toggle buttons to save
 * their open/close state when clicked.
 *
 * Args:
 *   sidebar: The sidebar HTMLElement.
 *   isTemporaryExpansionRef: Object with isTemporaryExpansion property.
 *                            State is not saved during temporary (hover) expansion.
 */
export function initDropdownPersistence(
  sidebar: HTMLElement,
  isTemporaryExpansionRef: { isTemporaryExpansion: boolean },
): void {
  const toggleButtons = sidebar.querySelectorAll<HTMLElement>("[data-collapse-toggle]");

  for (const button of toggleButtons) {
    const dropdownId = button.getAttribute("data-collapse-toggle");
    if (!dropdownId) continue;

    button.addEventListener("click", () => {
      // Let Flowbite process the toggle first
      setTimeout(() => {
        // Only save state if not in temporary expansion mode (hover)
        if (isTemporaryExpansionRef.isTemporaryExpansion) return;

        const dropdown = document.getElementById(dropdownId);
        if (!dropdown) return;

        const isOpen = isDropdownOpen(dropdown);
        saveDropdownState(dropdownId, isOpen);
      }, FLOWBITE_TOGGLE_DELAY_MS);
    });
  }
}

/**
 * Waits for Flowbite to be initialized, then runs a callback.
 *
 * If Flowbite is already ready, runs the callback immediately.
 * Otherwise, waits for the "flowbite:initialized" event.
 *
 * Args:
 *   callback: Function to run when Flowbite is ready.
 */
export function onFlowbiteReady(callback: () => void): void {
  if (isFlowbiteReady() && document.readyState === "complete") {
    // Flowbite already initialized
    callback();
  } else {
    // Wait for initialization event
    document.addEventListener("flowbite:initialized", callback, {
      once: true,
    });
  }
}
